/**
 * 生图适配器层类型契约（T11a）。
 *
 * 与 T03 的 `src/core/providers` 同构：配置只描述「连哪里、用什么 key」，
 * 适配器只负责「把请求发出去、把结果统一成 GeneratedImage[]」。
 * 超时 / 重试 / 取消 / 错误规范化一律复用 `../providers/http`。
 */

/* ------------------------------------------------------------------ *
 * 请求与结果
 * ------------------------------------------------------------------ */

export interface ImageRequest {
  prompt: string;
  /** 例如 `1024x1024`、`1792x1024`；不传则由服务端决定。 */
  size?: string;
  /** 生成张数，默认 1（适配器会钳制到 1..10）。 */
  n?: number;
  signal?: AbortSignal;
}

/** 统一结果：无论服务端返回 b64 还是 URL，最终都落到 dataUrl。 */
export interface GeneratedImage {
  dataUrl: string;
  mime: string;
  revisedPrompt?: string;
}

export interface ImageAdapter {
  readonly id: string;
  readonly kind: ImageKind;
  generate(req: ImageRequest): Promise<GeneratedImage[]>;
}

/* ------------------------------------------------------------------ *
 * 配置
 * ------------------------------------------------------------------ */

export type ImageKind = 'openai-compatible' | 'custom';

export interface ImageConfig {
  id: string;
  kind: ImageKind;
  name: string;
  baseUrl: string;
  apiKeyRef: string;
  defaultModel?: string;
  enabled: boolean;
}

/** 运行期已解析出凭据的配置（apiKeyRef -> apiKey 由上层密钥存储负责）。 */
export interface ResolvedImageConfig extends ImageConfig {
  apiKey?: string;
  /** 追加到默认请求头上的自定义头（例如自建网关的鉴权头）。 */
  headers?: Record<string, string>;
  /** 覆盖适配器默认超时（毫秒）。 */
  timeoutMs?: number;
  /** 覆盖适配器默认重试次数（不含首次请求，默认 1）。 */
  retries?: number;
  /** 覆盖适配器默认重试间隔（毫秒）。 */
  retryDelayMs?: number;
}

/** 可注入的 fetch 实现（默认取 globalThis.fetch）。 */
export type FetchLike = (input: string | URL, init?: RequestInit) => Promise<Response>;

/** 适配器可复用的运行期依赖，与 ProviderDeps 结构一致。 */
export interface ImageDeps {
  fetch?: FetchLike;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  extraHeaders?: Record<string, string>;
}

/** 适配器工厂：注册表按 kind 实例化适配器。 */
export type ImageAdapterFactory = (
  config: ResolvedImageConfig,
  deps?: ImageDeps,
) => ImageAdapter;

/* ------------------------------------------------------------------ *
 * 错误
 * ------------------------------------------------------------------ */

/** 错误码枚举，取值与 T03 `ProviderErrorCode` 保持一致。 */
export type ImageErrorCode =
  | 'http'
  | 'timeout'
  | 'network'
  | 'aborted'
  | 'parse'
  | 'config'
  | 'unsupported'
  | 'unknown';

export class ImageError extends Error {
  readonly code: ImageErrorCode;
  readonly status?: number;

  constructor(message: string, code: ImageErrorCode = 'unknown', status?: number) {
    super(message);
    this.name = 'ImageError';
    this.code = code;
    this.status = status;
  }
}

/**
 * 取消专用错误。
 *
 * 与 T03 对话适配器的「静默结束迭代」不同，`generate()` 是 Promise 接口，
 * 没有可静默结束的迭代，因此契约是**以 AbortError 结束**：
 * `error.name === 'AbortError'` 且 `code === 'aborted'`，调用方据此区分
 * 「用户取消」与「真实失败」。
 */
export class ImageAbortError extends ImageError {
  constructor(message = '生图请求已被取消') {
    super(message, 'aborted');
    this.name = 'AbortError';
  }
}

/** 取消判定：兼容原生 DOMException 与自定义 ImageAbortError。 */
export function isImageAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}